import React, { useState, useEffect, useContext } from "react";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import Modal from "react-modal";
import { message, Pagination } from "antd";
import Loader from "../Loader/Loader";
import { useNavigate } from "react-router";
import { UserContext } from "../../App";
import CustomModal from "../Modals/CustomModal";

import {
  getEquipment,
  createEquipment,
  updateEquipment,
  deleteEquipment,
} from "../../webServices/Apis";

function Equipment() {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useContext(UserContext);
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [equipmentName, setEquipmentName] = useState("");
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchEquipments();
  }, []);

  const fetchEquipments = async () => {
    try {
      const response = await getEquipment();
      const reverseEquipments = [...response?.data?.data].reverse();
      setEquipments(reverseEquipments);
    } catch (error) {
      console.error("Error fetching equipments:", error);
      if (error.message === 'Network Error' || error.message.includes('ERR_INTERNET_DISCONNECTED')) {
             // Handle internet disconnection
             return;
           }
           // Handle specific error cases
           if (error?.response?.status === 401 ) {
             window.alert("Your account is logged in to another device, please login again !")
             localStorage.removeItem('token');
             setIsAuthenticated(false)
             window.location.href = "/login";
             return;
           }
           if (error?.response?.status === 500){
             message.error("Server not responding")
             return;
           }
           if (error?.response?.status === 403){
             window.alert("Your subcription has been expired")
             localStorage.clear()
             setIsAuthenticated(false) 
             window.location.href = "/login";
             return;
           }
           else {
            return;
          }

    } finally {
      setLoading(false);
    }
  };

  const handleCreateEquipment = async () => {
    setLoading(true);
    try {
      if (!equipmentName.trim()) {
        message.error("Equipment name is mandatory");
        return;
      }

      if (equipmentName.length > 25) {
        message.error("Equipment name should be less than 25 characters")
        return;
      }
      const newEquipment = await createEquipment({ equipmentName });
      if (newEquipment.data.success) {
        const createdEquipment = newEquipment.data.data;
        setEquipments((prevEquipments) => [...prevEquipments, createdEquipment]);
        message.success(newEquipment.data.message);
      }
      closeCreateModal();
    } catch (error) {
      console.error("Error creating equipment:", error);
      message.error(error?.response?.data?.message || "Error while creating equipment");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEquipment = async () => {
    if (!equipmentName.trim()) {
      message.error("Equipment name is mandatory");
      return;
    }
    if(equipmentName.length > 25){
      message.error("Equiment name should be less than 25 characters")
      return;
    }
    setLoading(true);
    try {
      const id = selectedEquipment.id;
      const updatedEquipment = await updateEquipment(id, equipmentName);
      if (updatedEquipment.data.success) {
        setEquipments((prev) =>
          prev.map((e) =>
            e.id === id ? { ...e, equipmentName: updatedEquipment.data.data.equipmentName } : e
          )
        );
        message.success(updatedEquipment.data.message);
      }
      closeUpdateModal();
    } catch (error) {
      console.error("Error updating equipment:", error);
      message.error(error?.response?.data?.message || "Error while updating equipment");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEquipment = async () => {
    setLoading(true);
    try {
      const response = await deleteEquipment(equipmentToDelete);
      if (response.data.success) {
        setEquipments((prevEquipments) =>
          prevEquipments.filter((equipment) => equipment.id !== equipmentToDelete)
        );
        message.success(response.data.message);
      }
      closeConfirmDeleteModal();
    } catch (error) {
      console.error("Error deleting equipment:", error);
      message.error(error?.response?.data?.message || "Error while deleting equipment");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const openCreateModal = () => {
    setEquipmentName("");
    setIsCreateModalOpen(true);
  };

  const openUpdateModal = (equipment) => {
    setSelectedEquipment(equipment);
    setEquipmentName(equipment.equipmentName);
    setIsUpdateModalOpen(true);
  };

  const openConfirmDeleteModal = (id) => {
    setEquipmentToDelete(id);
    setIsConfirmDeleteModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setEquipmentName("");
  };

  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setSelectedEquipment(null);
    setEquipmentName("");
  };

  const closeConfirmDeleteModal = () => {
    setIsConfirmDeleteModalOpen(false);
    setEquipmentToDelete(null);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEquipments = equipments.slice(indexOfFirstItem, indexOfLastItem)

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex flex-row justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Equipments</h2>
        <button
          onClick={openCreateModal}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-12 rounded"
        >
          Add
        </button>
      </div>

      <table className="min-w-full bg-white border border-black border-b-4 rounded-lg">
        <thead className="bg-gray-800 text-white">
          <tr>
            <th className="py-3 px-4 text-center text-lg">Sr No</th>
            <th className="py-3 px-4 text-center text-lg">Name</th>
            <th className="py-3 px-4 text-center text-lg">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentEquipments?.length > 0 ? (
            currentEquipments.map((equipment, index) => (
              <tr
                key={equipment.id}
                className={`${index % 2 === 0 ? "bg-white" : "bg-gray-300"} hover:bg-gray-200 transition duration-200`}
              >
                <td className="py-4 px-8 border-b text-lg text-center">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td className="py-4 px-8 border-b text-lg text-center">{equipment.equipmentName}</td>
                <td className="py-1 px-6 mt-4 flex justify-center items-center text-lg">

                  <button
                    onClick={() => openUpdateModal(equipment)}
                    className="text-blue-600 hover:text-blue-800 flex items-center transition duration-200 text-lg"
                    aria-label="Edit Equipment"
                  >
                    <AiFillEdit className="text-xl" />
                  </button>
                  <button
                    onClick={() => openConfirmDeleteModal(equipment.id)}
                    className="text-red-600 hover:text-red-800 ml-2 flex items-center transition duration-200 text-lg"
                    aria-label="Delete Equipment"
                  >
                    <AiFillDelete className="text-xl" />
                  </button>

                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" className="text-center py-4">
                No equipments found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-4 flex justify-end">
        <Pagination
          current={currentPage}
          total={equipments?.length}
          pageSize={itemsPerPage}
          onChange={handlePageChange}
        />
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onRequestClose={closeCreateModal}
        className="bg-white rounded-lg shadow-lg max-w-lg h-auto overflow-auto w-[400px]"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        <div className="bg-blue-950 h-16 flex items-center justify-center rounded-t-lg">
          <h2 className="text-1xl font-bold text-white">Add Equipment</h2>
        </div>
        <div className="p-6">
          <label htmlFor="equipment-name" className="block text-sm font-bold text-gray-700">
            Equipment Name
          </label>
          <input
            id="equipment-name"
            type="text"
            placeholder="Enter Equipment Name"
            value={equipmentName}
            onChange={(e) => setEquipmentName(e.target.value)}
            className="border-2 border-blue-300 p-4 mt-1 w-full rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4 text-sm"
          />
          <div className="flex justify-center mt-6 gap-4">
            <button
              onClick={closeCreateModal}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-md transition duration-200 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateEquipment}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-8 rounded-md transition duration-200 text-sm"
            >
              Add
            </button>
          </div>
        </div>
      </Modal>

      {/* Update Modal */}
      <Modal
        isOpen={isUpdateModalOpen}
        onRequestClose={closeUpdateModal}
        className="bg-white rounded-lg shadow-lg max-w-lg h-auto overflow-auto w-[400px]"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        <div className="bg-blue-950 h-16 flex items-center justify-center rounded-t-lg">
          <h2 className="text-1xl font-bold text-white">Edit Equipment</h2>
        </div>
        <div className="p-6">
          <label htmlFor="equipment-name" className="block text-sm font-bold text-gray-700">
            Equipment Name
          </label>
          <input
            id="equipment-name"
            type="text"
            placeholder="Enter Equipment Name"
            value={equipmentName}
            onChange={(e) => setEquipmentName(e.target.value)}
            className="border-2 border-blue-300 p-4 mt-1 w-full rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4 text-sm"
          />
          <div className="flex justify-center mt-6 gap-4">
            <button
              onClick={closeUpdateModal}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-md transition duration-200 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateEquipment}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-8 rounded-md transition duration-200 text-sm"
            >
              Update
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={isConfirmDeleteModalOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this equipment ?"
        onConfirm={handleDeleteEquipment}
        onClose={closeConfirmDeleteModal}
      />
    </div>
  );
}

export default Equipment;
